import type { EmployeeFormValues } from "../schema/employee-schema";
import { apiFetch, apiUrl, OFFLINE_MESSAGE } from "./api";

export type PlatformStats = {
  totalUsers: number;
  totalCompanies: number;
  activeSessions: number;
  bannedUsers: number;
};

export type AdminUser = {
  id: string;
  name: string;
  email: string;
  image: string | null;
  role: string | null;
  /** Admin-dashboard section grants; only meaningful when role is "moderator". */
  moderatorSections: string[];
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  banned: boolean;
  banReason: string | null;
  banExpires: string | null;
  companyCount: number;
  activeSessionCount: number;
  /** The account's open trial, if any. */
  trial: { plan: string; endsAt: string; source: string | null } | null;
  /** Sample employees still in the companies the user owns. */
  sampleEmployees: number;
  createdAt: string;
  updatedAt: string;
};

export type AdminCompany = {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  memberCount: number;
  ownerUserId: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  createdAt: string;
};

export type AdminCompanyEmployee = {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  department: string;
  email: string | null;
  employmentStatus: string;
};

export type AdminCompanyMember = {
  userId: string;
  name: string;
  email: string;
  role: string;
};

export type AdminCompanyDetail = AdminCompany & {
  members: AdminCompanyMember[];
  employeeCount: number;
  employees: AdminCompanyEmployee[];
};

export type AdminSubscription = {
  id: string;
  status: string;
  planSlug: string | null;
  /** Attendance sessions created this month; null when the plan has no
   *  attendance. `custom` when the account has its own admin-set limit. */
  attendance: { used: number; limit: number; custom: boolean } | null;
  planName: string;
  amountCents: number;
  currency: string;
  recurringInterval: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  customerName: string | null;
  customerEmail: string | null;
  customerExternalId: string | null;
  /** Billable employees against the plan's limit — Business's included count,
   *  or the cap on every other plan — and this month's projected extra charge.
   *  Only monthly Business bills extras; `billsExtra` is false everywhere else.
   *  Null when the subscription isn't live or has no local account. */
  employees: {
    count: number;
    included: number;
    billableExtra: number;
    projectedOverageCents: number;
    billsExtra: boolean;
  } | null;
};

export type AdminSubscriptionsResult = {
  configured: boolean;
  items: AdminSubscription[];
  summary: {
    total: number;
    active: number;
    canceling: number;
    mrrCents: number;
    /** Projected per-employee charges this month across live subscriptions. */
    overageCents: number;
    byPlan: Record<string, number>;
  };
};

export type AdminUserAccount = {
  id: string;
  providerId: string;
  accountId: string;
  createdAt: string;
};

export type AdminPlanSummary = {
  slug: "free" | "starter" | "growth" | "business";
  name: string;
  employeeLimit: number;
  companyLimit: number;
  monthlyCredits: number;
  mostPopular: boolean;
  /** An open trial on the account; the fields above stay the paid
   *  plan. Only the user detail endpoint sends it. */
  trial?: { slug: AdminPlanSummary["slug"]; name: string; endsAt: string } | null;
};

export type AdminAvailablePlan = AdminPlanSummary & {
  priceCents: number;
  productConfigured: boolean;
};

export type AdminCompanyPlan = AdminPlanSummary & {
  companyId: string;
};

export type AdminUserDetail = AdminUser & {
  accounts: AdminUserAccount[];
  companies: AdminCompany[];
  personalPlan: AdminPlanSummary;
  companyPlans: AdminCompanyPlan[];
  subscriptions: AdminSubscription[];
  billingConfigured: boolean;
  availablePlans: AdminAvailablePlan[];
  attendance: AdminAttendanceUsage;
};

/** Attendance sessions an account has created this month against its limit.
 *  `limit` -1 = unlimited; `custom` = this account's own admin-set limit. */
export type AdminAttendanceUsage = {
  used: number;
  limit: number;
  custom: boolean;
  resetsAt: string;
};

/** Attendance sessions per month for each plan, as they currently apply. */
export type AttendancePlanLimits = Record<AdminPlanSummary["slug"], number>;

class AdminApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new AdminApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new AdminApiError(message, res.status);
  }
  return data as T;
}

export async function listAdminUsers(): Promise<AdminUser[]> {
  const res = await request<{ items: AdminUser[] }>("/api/admin/users");
  return res.items;
}

export async function getAdminUserDetail(userId: string): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(`/api/admin/users/${userId}`);
}

export async function updateModeratorSections(input: {
  userId: string;
  sections: string[];
}): Promise<string[]> {
  const res = await request<{ sections: string[] }>(
    `/api/admin/users/${input.userId}/moderator-sections`,
    {
      method: "PATCH",
      body: JSON.stringify({ sections: input.sections }),
    },
  );
  return res.sections;
}

export async function changeAdminUserPlan(input: {
  userId: string;
  subscriptionId: string;
  plan: AdminPlanSummary["slug"];
}): Promise<void> {
  await request(`/api/admin/users/${input.userId}/plan`, {
    method: "POST",
    body: JSON.stringify({
      subscriptionId: input.subscriptionId,
      plan: input.plan,
    }),
  });
}

export async function listAdminCompanies(): Promise<AdminCompany[]> {
  const res = await request<{ items: AdminCompany[] }>("/api/admin/companies");
  return res.items;
}

export async function getAdminCompany(companyId: string): Promise<AdminCompanyDetail> {
  return request<AdminCompanyDetail>(`/api/admin/companies/${companyId}`);
}

export async function reassignCompanyOwner(input: {
  companyId: string;
  userId: string;
}): Promise<AdminCompanyDetail> {
  return request<AdminCompanyDetail>(`/api/admin/companies/${input.companyId}/reassign`, {
    method: "POST",
    body: JSON.stringify({ userId: input.userId }),
  });
}

export async function getAdminSubscriptions(): Promise<AdminSubscriptionsResult> {
  return request<AdminSubscriptionsResult>("/api/admin/subscriptions");
}

/** Where an account stands with Polar. A `conflict` is a customer Polar holds
 *  for the account's email under another account's id: adopted when that
 *  account is gone and this one verified the email, otherwise left for an
 *  admin. */
export type PolarLink =
  | { status: "unconfigured" }
  | {
      status: "linked";
      customerId: string;
      /** The gone account an adopted customer was made for. */
      adoptedFrom: string | null;
    }
  | { status: "unlinked" }
  | {
      status: "conflict";
      customerId: string;
      heldBy: string;
      heldByExists: boolean;
      /** Subscriptions on that customer not yet canceled or expired. */
      liveSubscriptions: number;
      emailVerified: boolean;
      /** Syncing will adopt it: its account is gone and the email verified. */
      adoptable: boolean;
    };

export type PolarAccountSync = {
  link: PolarLink;
  planSlug: string;
  ordersGranted: number;
};

export type PolarBulkSync = {
  configured: boolean;
  unlinked: number;
  linked: number;
  conflicts: { userId: string; email: string; customerId: string; heldBy: string }[];
  failed: number;
  plansUpdated: number;
  ordersGranted: number;
};

export function getUserPolarLink(userId: string): Promise<PolarLink> {
  return request<PolarLink>(`/api/admin/users/${userId}/polar`);
}

/** Links the account's Polar customer, re-reads its plan and credits missed
 *  orders. Idempotent. */
export function syncUserWithPolar(userId: string): Promise<PolarAccountSync> {
  return request<PolarAccountSync>(`/api/admin/users/${userId}/polar/sync`, { method: "POST" });
}

/** Deletes the stale Polar customer blocking the account's email, then syncs.
 *  The API refuses unless that customer's account is gone and nothing on it
 *  still bills. */
export function releaseStalePolarCustomer(
  userId: string,
): Promise<PolarAccountSync & { removedCustomerId: string | null }> {
  return request(`/api/admin/users/${userId}/polar/release-stale`, { method: "POST" });
}

export function syncAllWithPolar(): Promise<PolarBulkSync> {
  return request<PolarBulkSync>("/api/admin/subscriptions/sync-all", { method: "POST" });
}

/** Every plan's monthly attendance session limit (admin only). */
export function getAttendanceLimits(): Promise<AttendancePlanLimits> {
  return request<AttendancePlanLimits>("/api/admin/attendance-limits");
}

/** Saves every plan's monthly attendance session limit (admin only). */
export function updateAttendanceLimits(
  limits: AttendancePlanLimits,
): Promise<AttendancePlanLimits> {
  return request<AttendancePlanLimits>("/api/admin/attendance-limits", {
    method: "PUT",
    body: JSON.stringify(limits),
  });
}

/** Sets one account's own attendance session limit, or with null returns it
 *  to its plan's limit (admin only). */
export function setUserAttendanceLimit(input: {
  userId: string;
  limit: number | null;
}): Promise<AdminAttendanceUsage> {
  return request<AdminAttendanceUsage>(`/api/admin/users/${input.userId}/attendance-limit`, {
    method: "PUT",
    body: JSON.stringify({ limit: input.limit }),
  });
}

/** A sign-up held for review because its address produced too many accounts. */
export type HeldSignup = {
  id: string;
  name: string;
  email: string;
  signupIp: string | null;
  createdAt: string;
};

export type SafeguardSettings = {
  /** Sign-ups allowed from one address in 24 hours before the next is held; 0 = off. */
  signupHoldThreshold: number;
  /** Domains refused at sign-up, on top of the bundled disposable list. */
  blockedEmailDomains: string[];
};

/** Sign-ups waiting for review (admin only). */
export async function listHeldSignups(): Promise<HeldSignup[]> {
  const data = await request<{ items: HeldSignup[] }>("/api/admin/safeguards/review");
  return data.items;
}

/** Clears a hold, or bans the account and signs it out (admin only). */
export function reviewSignup(input: {
  userId: string;
  decision: "approve" | "reject";
}): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/admin/safeguards/review/${input.userId}`, {
    method: "POST",
    body: JSON.stringify({ decision: input.decision }),
  });
}

export function getSafeguardSettings(): Promise<SafeguardSettings> {
  return request<SafeguardSettings>("/api/admin/safeguards/settings");
}

export function updateSafeguardSettings(input: SafeguardSettings): Promise<SafeguardSettings> {
  return request<SafeguardSettings>("/api/admin/safeguards/settings", {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export type UsageReportingMode = "off" | "dry_run" | "live";

export type PricingOverview = {
  usageReporting: UsageReportingMode;
  polarConfigured: boolean;
};

export type UsageReportRun = {
  mode: UsageReportingMode;
  sent: number;
  reports: { userId: string; employees: number }[];
};

/** The per-employee reporting mode and whether Polar is set up (admin only). */
export function getPricingOverview(): Promise<PricingOverview> {
  return request<PricingOverview>("/api/admin/pricing");
}

export function setUsageReportingMode(
  mode: UsageReportingMode,
): Promise<{ mode: UsageReportingMode }> {
  return request<{ mode: UsageReportingMode }>("/api/admin/pricing/usage-reporting", {
    method: "PUT",
    body: JSON.stringify({ mode }),
  });
}

/** Runs the daily headcount report now and returns what it counted. */
export function runUsageReport(): Promise<UsageReportRun> {
  return request<UsageReportRun>("/api/admin/pricing/usage-reporting/run", { method: "POST" });
}

export type QuotaKey =
  | "customTemplates"
  | "broadcastsPerCycle"
  | "publishedPolicies"
  | "attendanceSessionsPerCycle"
  | "employeeSigningPerCycle"
  | "publicSigningPerCycle"
  | "leaveEnrolledEmployees"
  | "assetItems"
  | "employeeFolders"
  | "businessFolders"
  | "recurringRemindersActive"
  | "openCases"
  | "trackedCredentials"
  | "dailyEmails";

export type PlanQuotaTable = Record<AdminPlanSummary["slug"], Record<QuotaKey, number>>;

export type BusinessMigration = {
  configured: true;
  dryRun: boolean;
  subscriptions: { id: string; customerExternalId: string | null; moved: boolean }[];
};

/** Moves Business subscribers onto the product with the metered per-employee
 *  price from their next renewal; `dryRun` only lists them (admin only). */
export function migrateBusinessSubscriptions(dryRun: boolean): Promise<BusinessMigration> {
  return request<BusinessMigration>("/api/admin/pricing/business-subscriptions/migrate", {
    method: "POST",
    body: JSON.stringify({ dryRun }),
  });
}

/** What a new account starts with, and each plan's monthly AI credits. */
export type SignupCreditSettings = {
  signup: {
    trialPlan: "business" | "growth" | "starter" | "none";
    trialDays: number;
    signupCredits: number;
    sampleEmployees: number;
  };
  credits: {
    monthly: Record<AdminPlanSummary["slug"], number>;
    perExtraEmployee: number;
  };
};

/** The saved signup and credit settings, and the code defaults beside them. */
export function getSignupCreditSettings(): Promise<{
  settings: SignupCreditSettings;
  defaults: SignupCreditSettings;
}> {
  return request("/api/admin/pricing/signup-credits");
}

export function saveSignupCreditSettings(
  settings: SignupCreditSettings,
): Promise<{ settings: SignupCreditSettings }> {
  return request("/api/admin/pricing/signup-credits", {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

export type TrialEmailStep = "reminder" | "last_day" | "ended" | "follow_up_1" | "follow_up_2";

/** One trial email. `day` is days before the end (reminder) or after it. */
export type TrialEmailSettings = {
  enabled: boolean;
  day: number;
  subject: string;
  headline: string;
  intro: string;
  buttonLabel: string;
  blocks: { usage: boolean; activity: boolean; offer: boolean };
};

/** The emails, banner and offer around a trial's end (Settings → Trial
 *  lifecycle). Validated by `@/schema/trial-lifecycle-schema`. */
export type TrialLifecycleSettings = {
  emails: Record<TrialEmailStep, TrialEmailSettings>;
  banner: {
    enabled: boolean;
    headline: string;
    body: string;
    buttonLabel: string;
    snoozeDays: number;
    showLost: boolean;
    showActivity: boolean;
    showOffer: boolean;
  };
  offer: { enabled: boolean; polarDiscountId: string; label: string; validDays: number };
};

export function getTrialLifecycleSettings(): Promise<{
  settings: TrialLifecycleSettings;
  defaults: TrialLifecycleSettings;
  polarConfigured: boolean;
}> {
  return request("/api/admin/pricing/trial-lifecycle");
}

export function saveTrialLifecycleSettings(
  settings: TrialLifecycleSettings,
): Promise<{ settings: TrialLifecycleSettings }> {
  return request("/api/admin/pricing/trial-lifecycle", {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

/** The Polar discounts a trial offer can use. */
export function listTrialOfferDiscounts(): Promise<{
  discounts: { id: string; name: string; code: string | null }[];
}> {
  return request("/api/admin/pricing/trial-lifecycle/discounts");
}

/** Emails one trial email, as the form stands, to the signed-in admin. */
export function sendTrialEmailPreview(
  settings: TrialLifecycleSettings,
  step: TrialEmailStep,
): Promise<{ sentTo: string }> {
  return request("/api/admin/pricing/trial-lifecycle/preview", {
    method: "POST",
    body: JSON.stringify({ settings, step }),
  });
}

/** One person on the sample roster a new account's employees start from:
 *  every field of the employee form. The API omits blank optional fields. */
export type SampleEmployee = Partial<EmployeeFormValues>;

/** The saved sample roster, the code default and the most it can hold. */
export function getSampleRoster(): Promise<{
  roster: SampleEmployee[];
  defaults: SampleEmployee[];
  max: number;
}> {
  return request("/api/admin/pricing/sample-employees");
}

export function saveSampleRoster(roster: SampleEmployee[]): Promise<{ roster: SampleEmployee[] }> {
  return request("/api/admin/pricing/sample-employees", {
    method: "PUT",
    body: JSON.stringify({ roster }),
  });
}

/** Every plan's number for every quota. */
export function getPlanQuotas(): Promise<{ plans: PlanQuotaTable }> {
  return request<{ plans: PlanQuotaTable }>("/api/admin/pricing/quotas");
}

/** Sets one quota for every plan. */
export function setPlanQuota(input: {
  key: QuotaKey;
  limits: Record<AdminPlanSummary["slug"], number>;
}): Promise<unknown> {
  return request(`/api/admin/pricing/quotas/${input.key}`, {
    method: "PUT",
    body: JSON.stringify({ limits: input.limits }),
  });
}

/** One account's own quota numbers. */
export async function getAccountQuotas(userId: string): Promise<Partial<Record<QuotaKey, number>>> {
  const data = await request<{ overrides: Partial<Record<QuotaKey, number>> }>(
    `/api/admin/pricing/accounts/${userId}/quotas`,
  );
  return data.overrides;
}

/** Sets (or with null clears) one account's own number for one quota. */
export async function setAccountQuota(input: {
  userId: string;
  key: QuotaKey;
  limit: number | null;
}): Promise<Partial<Record<QuotaKey, number>>> {
  const data = await request<{ overrides: Partial<Record<QuotaKey, number>> }>(
    `/api/admin/pricing/accounts/${input.userId}/quotas/${input.key}`,
    { method: "PUT", body: JSON.stringify({ limit: input.limit }) },
  );
  return data.overrides;
}

export { AdminApiError };

/**
 * The platform headline counters for the admin Overview cards.
 *
 * The Next.js app fetched this in `getServerAdminStats` so the cards rendered
 * with real numbers on first paint; the admin Overview route loader now does
 * the same job. Returns `null` rather than throwing when the backend is
 * unreachable, so the page renders dashes instead of an error screen — the
 * behaviour the Overview's `stats === null` branch already expects.
 */
export async function getPlatformStats(): Promise<PlatformStats | null> {
  try {
    const res = await apiFetch(apiUrl("/api/admin/stats"), {
      method: "GET",
      cache: "no-store",
    });
    if (!res.ok) return null;
    return (await res.json()) as PlatformStats;
  } catch (err) {
    console.error("[admin] backend unreachable", err);
    return null;
  }
}

export const platformStatsQuery = {
  queryKey: ["admin", "stats"] as const,
  queryFn: getPlatformStats,
};
